import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/services/apiGateway';
import { useAuthStore } from '@/store/authStore';
import { AiMarkdownRenderer } from '@/components/ui/AiMarkdownRenderer';

interface Message {
  role: 'ai' | 'user';
  text: string;
}

interface ExplainResponse {
  explanation: string;
  subject: string;
  topic: string;
}

interface SessionResponse {
  session_id: string;
  streak_count: number;
}

export function ExplainSession() {
  const [conversation, setConversation] = useState<Message[]>([]);
  const [isLoading, setIsLoading]       = useState(false);
  const [error, setError]               = useState<string | null>(null);

  // Theme-input gate: shown when no theme is pre-filled in the URL
  const [themeInput, setThemeInput]     = useState('');
  const [hasStarted, setHasStarted]     = useState(false);

  const scrollRef       = useRef<HTMLDivElement>(null);
  const sessionStartRef = useRef<number>(Date.now());
  const sessionLoggedRef = useRef(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subject   = searchParams.get('subject') ?? '';
  const urlTheme  = searchParams.get('theme')   ?? '';

  // Active theme: from URL if provided, otherwise from the input gate
  const [activeTheme, setActiveTheme]   = useState(urlTheme);

  const { childId, setStreakCount } = useAuthStore();
  const { t } = useTranslation();

  // Scroll chat to bottom whenever conversation updates
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [conversation, isLoading]);

  // Helper: call /ai/explain and append the response as an AI message
  const fetchExplanation = async (topicOverride: string) => {
    setIsLoading(true);
    try {
      const res = await apiFetch<ExplainResponse>('/ai/explain', {
        method: 'POST',
        body: JSON.stringify({ subject, topic: topicOverride }),
      });
      setConversation((prev) => [...prev, { role: 'ai', text: res.explanation }]);
      setError(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('explain.generate_failed');
      setError(msg.includes('429') ? t('ai_session.limit_reached') : msg);
    } finally {
      setIsLoading(false);
    }
  };

  // Start the session: if a theme was pre-filled in the URL, begin immediately.
  // Otherwise wait for the student to submit the theme-input gate.
  useEffect(() => {
    if (urlTheme) {
      sessionStartRef.current = Date.now();
      setHasStarted(true);
      void fetchExplanation(urlTheme || subject);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally only on mount

  const handleStartExplain = () => {
    const topic = themeInput.trim();
    if (!topic) return;
    setActiveTheme(topic);
    setHasStarted(true);
    sessionStartRef.current = Date.now();
    void fetchExplanation(topic);
  };

  // Log session and update streak
  const logSession = () => {
    if (sessionLoggedRef.current || !childId) return;
    sessionLoggedRef.current = true;
    const elapsedMin = Math.round((Date.now() - sessionStartRef.current) / 60_000);
    apiFetch<SessionResponse>(`/children/${childId}/sessions`, {
      method: 'POST',
      body: JSON.stringify({
        subject,
        topic: activeTheme || subject,
        format: 'short',
        duration_minutes: Math.max(5, Math.min(240, elapsedMin || 10)),
      }),
    })
      .then((res) => setStreakCount(res.streak_count))
      .catch(() => { /* best-effort */ });
  };

  const handleGotIt = () => {
    logSession();
    navigate('/student');
  };

  const handleDeepDive = () => {
    const userText = t('explain.deep_dive_msg');
    setConversation((prev) => [...prev, { role: 'user', text: userText }]);
    void fetchExplanation(
      `${activeTheme || subject} — provide an advanced deep dive with more technical detail, mechanisms, and worked examples`,
    );
  };

  const handleSimpler = () => {
    const userText = t('explain.simpler_msg');
    setConversation((prev) => [...prev, { role: 'user', text: userText }]);
    void fetchExplanation(
      `${activeTheme || subject} — re-explain this in the simplest possible terms, using everyday analogies, for someone with no background knowledge`,
    );
  };

  return (
    <div className="page-center">
      <div className="phone-mockup" style={{ display: 'flex', flexDirection: 'column' }}>

        {/* Top navigation bar */}
        <div style={{
          background: 'white', borderBottom: '1px solid var(--border)',
          padding: '45px 20px 12px', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
        }}>
          <button
            type="button"
            onClick={() => navigate('/student')}
            style={{
              background: 'transparent', border: 'none', color: 'var(--primary)',
              fontSize: '0.95rem', fontFamily: 'inherit', fontWeight: 600,
              cursor: 'pointer', padding: 0,
            }}
          >
            {t('explain.leave')}
          </button>
          <div style={{ textAlign: 'center' }}>
            <span style={{ fontWeight: 700, color: 'var(--text-dark)', fontSize: '0.9rem', display: 'block' }}>
              {subject}
            </span>
            {activeTheme && (
              <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{activeTheme}</span>
            )}
          </div>
          <span style={{ width: '80px' }} />
        </div>

        {/* ── Theme-input gate (no theme in URL) ── */}
        {!hasStarted && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '24px 20px', gap: '16px' }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '2rem', margin: '0 0 8px' }}>💬</p>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 4px' }}>
                {subject}
              </h2>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: 0 }}>
                {t('explain.prompt_question')}
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)' }}>
                {t('explain.topic_theme')}
              </label>
              <input
                type="text"
                className="input"
                placeholder={t('explain.topic_placeholder')}
                value={themeInput}
                onChange={(e) => setThemeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleStartExplain(); }}
                autoFocus
              />
              <button
                type="button"
                className="btn btn-primary"
                disabled={!themeInput.trim()}
                onClick={handleStartExplain}
                style={{ width: '100%' }}
              >
                {t('explain.ask_btn')}
              </button>
            </div>
          </div>
        )}

        {/* Scrollable chat area */}
        {hasStarted && <div
          ref={scrollRef}
          style={{
            flex: 1, overflowY: 'auto', padding: '16px 20px',
            display: 'flex', flexDirection: 'column', gap: '12px',
          }}
        >
          {conversation.map((msg, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <div style={{
                maxWidth: '85%',
                padding: '12px 14px',
                borderRadius: msg.role === 'ai' ? '4px 14px 14px 14px' : '14px 4px 14px 14px',
                background: msg.role === 'ai' ? 'var(--primary-light)' : '#F3F4F6',
                color: 'var(--text-dark)',
                fontSize: '0.9rem',
                lineHeight: 1.6,
              }}>
                {msg.role === 'ai' ? <AiMarkdownRenderer content={msg.text} /> : msg.text}
              </div>
            </div>
          ))}

          {/* Typing indicator */}
          {isLoading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div style={{
                padding: '10px 14px',
                borderRadius: '4px 14px 14px 14px',
                background: 'var(--primary-light)',
                display: 'flex', alignItems: 'center', gap: '4px',
              }}>
                {[0, 1, 2].map((dot) => (
                  <span
                    key={dot}
                    style={{
                      width: '6px', height: '6px', borderRadius: '50%',
                      background: 'var(--primary)', display: 'inline-block',
                      animation: `skeleton-pulse 1.2s ease-in-out ${dot * 0.2}s infinite`,
                    }}
                  />
                ))}
                <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginLeft: '6px' }}>
                  {t('explain.claude_typing')}
                </span>
              </div>
            </div>
          )}

          {error && (
            <p style={{ color: 'var(--danger)', fontSize: '0.875rem', textAlign: 'center', margin: 0 }}>
              {error}
            </p>
          )}
        </div>}

        {/* Sticky action bar */}
        {hasStarted && !isLoading && !error && conversation.length > 0 && (
          <div style={{
            flexShrink: 0, padding: '12px 20px 20px',
            borderTop: '1px solid var(--border)', background: 'white',
            display: 'flex', flexDirection: 'column', gap: '8px',
          }}>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleGotIt}
              style={{ width: '100%' }}
            >
              ✅ {t('explain.got_it')}
            </button>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={handleDeepDive}
                style={{ flex: 1, fontSize: '0.85rem' }}
              >
                🤿 {t('explain.deep_dive')}
              </button>
              <button
                type="button"
                className="btn btn-outline"
                onClick={handleSimpler}
                style={{ flex: 1, fontSize: '0.85rem' }}
              >
                🤔 {t('explain.simpler')}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
